import ApiRequest from "@/common/lib/apiRequest";
import { hexlify } from "@/common/lib/utils";

const mutationTypes = {
  SET_AVAILABLE_BALANCE: "SET_AVAILABLE_BALANCE",
  SET_ALLOWANCE: "SET_ALLOWANCE",
  RESET_STATE: "RESET_STATE",
  SET_TX_ACTION: "SET_TX_ACTION",
};

const initState = () => ({
  availableBalance: {
    result: {
      balance: 0,
    },
    fetching: false,
  },
  allowance: {
    fetching: false,
    result: null,
    error: null,
  },
  currentTxAction: "",
});

const state = initState();

const actions = {
  resetState({ commit }) {
    commit(mutationTypes.RESET_STATE);
  },
  async fetchRequiredInfo({ dispatch, rootGetters }) {
    if (rootGetters["userWallets/secondaryWalletSelected"].coin_network !== "ethereum") {
      return;
    }
    return Promise.all([
      dispatch("fetchAllowance"),
      dispatch("fetchAvailableBalance"),
    ]);
  },
  async fetchAvailableBalance({ commit, rootGetters }) {
    const address = rootGetters["userWallets/secondaryWalletSelected"].address;
    commit(mutationTypes.SET_AVAILABLE_BALANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "/eth/dydx/available_balance/" + address
      );
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { result, fetching: false });
    } catch (error) {
      console.log('ERROR', error);
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { error, fetching: false });
    }
  },
  async fetchAllowance({ commit, rootGetters }, address) {
    const wallet = rootGetters["userWallets/secondaryWalletSelected"];
    address = address || wallet?.address;
    if (!address) return;
    commit(mutationTypes.SET_ALLOWANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "eth/dydx/allowance/" + address
      );
      if (address.toLowerCase() === wallet.address?.toLowerCase()) {
        commit(mutationTypes.SET_ALLOWANCE, { result, fetching: false });
      }
      return result;
    } catch (error) {
      commit(mutationTypes.SET_ALLOWANCE, { error, fetching: false });
    }
  },
  async getNonce({ rootGetters }) {
    const address = rootGetters["userWallets/secondaryWalletSelected"].address;
    const result = await ApiRequest.get(`/eth/dydx/nonce/${address}`);
    return result.value;
  },
  async getApprovalRawTx({ rootGetters, dispatch }) {
    const address = rootGetters["userWallets/secondaryWalletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/dydx/digest/approve/${address}`),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getBridgeRawTx({ rootGetters, dispatch }, { amount, destinationAddr }) {
    const address = rootGetters["userWallets/secondaryWalletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/dydx/digest/bridge/${address}`, {
        params: { amount, dest_addr: destinationAddr },
      }),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async broadcastTx({ rootGetters }, payload) {
    const wallet = rootGetters["userWallets/secondaryWalletSelected"];
    let url;
    let body = {
      type: payload.type,
      metadata: payload.metadata,
    };
    if (payload.signedTx) {
      body.signed_tx = payload.signedTx;
      url = `/eth/dydx/transaction/broadcast/${wallet.address}`;
    } else {
      body.tx_hash = payload.txHash;
      url = `/eth/dydx/transaction/create/${wallet.address}`;
    }
    return ApiRequest.post(url, body);
  },
  resetState({ commit }) {
    commit(mutationTypes.RESET_STATE);
  },
  setTxAction({ commit }, action) {
    commit(mutationTypes.SET_TX_ACTION, action);
  },
};

const mutations = {
  [mutationTypes.SET_AVAILABLE_BALANCE](state, data) {
    state.availableBalance = {
      ...state.availableBalance,
      ...data,
    };
  },
  [mutationTypes.SET_TX_ACTION](state, action) {
    state.currentTxAction = action;
  },
  [mutationTypes.RESET_STATE](state) {
    const stakingState = initState();
    for (const key in stakingState) {
      state[key] = stakingState[key];
    }
    state.currentTxAction = "";
  },
  [mutationTypes.SET_ALLOWANCE](state, data) {
    state.allowance = {
      ...state.allowance,
      ...data,
    };
  },
};

const getters = {
  currentTxAction: state => state.currentTxAction,
  availableBalance: state => state.availableBalance,
  needUnlockAssets: state => {
    return state.allowance.result?.value == 0 ?? false;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
